import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellOff, CheckCheck } from "lucide-react";

import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/hint";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  notificationTarget,
  type AppNotification,
} from "@/lib/api/notifications";
import { cn, formatDateTime, formatRelativeTime } from "@/lib/utils";

import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationList,
  useUnreadNotificationCount,
} from "./useNotifications";

/** Wider than this, a badge is a smear; the button's name keeps the exact count. */
const BADGE_CAP = 99;

/**
 * The header bell: the signed-in user's in-app notifications.
 *
 * The badge is the server's unread count and nothing else. It is absent while
 * the count is loading, when the last read of it failed, and at zero, so the
 * chrome never carries a number the API did not just give.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const headingId = useId();
  const count = useUnreadNotificationCount();
  const unread = count.status === "success" ? count.data : null;
  const showBadge = unread != null && unread > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 text-muted-foreground hover:text-foreground"
          aria-label={
            showBadge ? `Notifications, ${unread} unread` : "Notifications"
          }
        >
          <Bell className="h-4 w-4" aria-hidden />
          {showBadge && (
            <span
              aria-hidden
              className="absolute right-1 top-1 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums leading-none text-primary-foreground"
            >
              {unread > BADGE_CAP ? `${BADGE_CAP}+` : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-labelledby={headingId}
        className="w-[min(24rem,calc(100vw-2rem))] p-0"
      >
        <NotificationPanel
          headingId={headingId}
          unread={unread}
          onNavigate={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

function NotificationPanel({
  headingId,
  unread,
  onNavigate,
}: {
  headingId: string;
  unread: number | null;
  onNavigate: () => void;
}) {
  const list = useNotificationList();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const rows = list.data?.results ?? [];
  const anyUnread = (unread ?? 0) > 0 || rows.some((n) => !n.is_read);

  return (
    <div className="flex max-h-[min(32rem,calc(100vh-6rem))] flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
        <h2 id={headingId} className="text-sm font-medium text-foreground">
          Notifications
        </h2>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={!anyUnread || markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          <CheckCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Mark all read
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isPending ? (
          <PanelLoading />
        ) : list.isError ? (
          <div className="px-4">
            <ErrorState
              size="compact"
              title="Notifications could not load"
              description="The request failed. Try again in a moment."
              onRetry={() => void list.refetch()}
            />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4">
            <EmptyState
              size="compact"
              icon={BellOff}
              title="No notifications yet"
              description="Alerts, Desk updates and notices sent to you will appear here."
            />
          </div>
        ) : (
          <ul>
            {rows.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onMarkRead={() => markRead.mutate(n.id)}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        )}
      </div>

      {list.data && list.data.total > rows.length && (
        <p className="border-t border-border/60 px-4 py-2 text-xs text-muted-foreground">
          Showing the {rows.length} most recent of {list.data.total}.
        </p>
      )}
    </div>
  );
}

function NotificationRow({
  notification: n,
  onMarkRead,
  onNavigate,
}: {
  notification: AppNotification;
  onMarkRead: () => void;
  onNavigate: () => void;
}) {
  const target = notificationTarget(n);
  const isRead = n.is_read === true;

  return (
    <li className="border-b border-border/40 last:border-b-0">
      <div
        className={cn("flex gap-3 px-4 py-3", !isRead && "bg-primary/[0.04]")}
      >
        {/* The dot is the sighted cue; the hidden word is the same fact for a
            screen reader, so read state never rests on colour alone. */}
        <span
          aria-hidden
          className={cn(
            "mt-1.5 h-2 w-2 shrink-0 rounded-full",
            isRead ? "bg-transparent" : "bg-primary",
          )}
        />
        <div className="min-w-0 flex-1 space-y-1">
          <p
            className={cn(
              "break-words text-sm",
              isRead ? "text-muted-foreground" : "font-medium text-foreground",
            )}
          >
            {!isRead && <span className="sr-only">Unread: </span>}
            {n.title}
          </p>
          {n.message && (
            <p className="break-words text-xs leading-relaxed text-muted-foreground">
              {n.message}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5 text-xs">
            <Hint content={formatDateTime(n.created)}>
              <time dateTime={n.created} className="text-muted-foreground">
                {formatRelativeTime(n.created)}
              </time>
            </Hint>
            {target && (
              <Link
                to={target}
                onClick={() => {
                  if (!isRead) onMarkRead();
                  onNavigate();
                }}
                className="font-medium text-primary underline-offset-2 hover:underline"
              >
                {n.action_text || "Open"}
              </Link>
            )}
            {!isRead && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                aria-label={`Mark "${n.title}" read`}
                onClick={onMarkRead}
              >
                Mark read
              </Button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function PanelLoading() {
  return (
    <div aria-busy="true" className="space-y-4 px-4 py-4">
      <span className="sr-only">Loading notifications</span>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex gap-3">
          <Skeleton className="mt-1 h-2 w-2 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}
